import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BakimFiyatTopluAtaModal from '../components/BakimFiyatTopluAtaModal';
import { fetchCategories } from '../services/categoriesService';
import {
  fetchAllBakimFiyatlariForCategory,
  fetchBakimFiyatlari,
  updateBakimFiyati,
  updateBakimFiyatiToplu,
} from '../services/bakimFiyatService';
import { buildCategoryTree } from '../utils/categoryTreeUtils';
import './BakimYonetimiPage.css';

const UNCategorized_KEY = '__none__';

const formatCurrency = (val) => {
  if (val == null || !Number.isFinite(Number(val))) return '—';
  return `${Number(val).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
};

const getErrorMessage = (err, fallback) =>
  err?.message || err?.data?.mesaj || err?.data?.message || fallback;

const getRowId = (row) => String(row.id || row.icerikId);

const mergeCategoriesDedup = (prev, incoming) => {
  const byId = new Map();
  (prev || []).forEach((c) => {
    const id = c.id || c._id;
    if (id != null && id !== '') byId.set(String(id), c);
  });
  (incoming || []).forEach((c) => {
    const id = c.id || c._id;
    if (id != null && id !== '') byId.set(String(id), c);
  });
  return Array.from(byId.values());
};

const getDescendantCategoryIdsFromFlat = (rootId, categories) => {
  const root = String(rootId);
  const result = new Set([root]);
  let changed = true;
  while (changed) {
    changed = false;
    categories.forEach((c) => {
      const id = String(c.id || c._id);
      const parent = c.ustKategoriId ? String(c.ustKategoriId) : null;
      if (parent && result.has(parent) && !result.has(id)) {
        result.add(id);
        changed = true;
      }
    });
  }
  return Array.from(result);
};

const CategoryCheckbox = ({ checked, indeterminate, disabled, onChange, ariaLabel }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);
  const stopRowToggle = (e) => e.stopPropagation();
  return (
    <input
      ref={ref}
      type="checkbox"
      className="ctp-bulk-check-input bakim-cat-check"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      onClick={stopRowToggle}
      onPointerDown={stopRowToggle}
      aria-label={ariaLabel}
    />
  );
};

const BakimYonetimiPage = () => {
  const [categories, setCategories] = useState([]);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState(() => new Set());
  const [confirmedLeafCategoryIds, setConfirmedLeafCategoryIds] = useState(() => new Set());
  const [productsByCategoryId, setProductsByCategoryId] = useState({});
  const [leafContentsLoadingIds, setLeafContentsLoadingIds] = useState(() => new Set());
  const [categorySelectLoadingIds, setCategorySelectLoadingIds] = useState(() => new Set());
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  const [arama, setArama] = useState('');
  const [debouncedArama, setDebouncedArama] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ bakimFiyati: '', fabrikaPayi: '' });
  const [saving, setSaving] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);

  const fetchedCategoryChildrenRef = useRef(new Set());
  const contentsLoadedForCategoryRef = useRef(new Set());
  const subtreeProductIdsRef = useRef(new Map());

  useEffect(() => {
    const t = setTimeout(() => setDebouncedArama(arama.trim()), 300);
    return () => clearTimeout(t);
  }, [arama]);

  const isSearchActive = debouncedArama.length >= 1;

  const loadRootCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const res = await fetchCategories({ kategoriTipi: 'urun', ustKategoriId: 'root' });
      const list = res?.veri?.kategoriler || res?.veri || [];
      setCategories(Array.isArray(list) ? list : []);
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Kategoriler yüklenemedi'));
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRootCategories();
  }, [loadRootCategories]);

  const loadProductsForCategory = useCallback(async (categoryIdStr, { force = false } = {}) => {
    if (!categoryIdStr) return [];
    if (force) contentsLoadedForCategoryRef.current.delete(categoryIdStr);
    if (!force && contentsLoadedForCategoryRef.current.has(categoryIdStr)) {
      return productsByCategoryId[categoryIdStr] || [];
    }

    setLeafContentsLoadingIds((prev) => new Set(prev).add(categoryIdStr));
    try {
      const aggregated = await fetchAllBakimFiyatlariForCategory(categoryIdStr);
      setProductsByCategoryId((prev) => ({ ...prev, [categoryIdStr]: aggregated }));
      contentsLoadedForCategoryRef.current.add(categoryIdStr);
      return aggregated;
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Ürünler yüklenemedi'));
      return [];
    } finally {
      setLeafContentsLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(categoryIdStr);
        return next;
      });
    }
  }, [productsByCategoryId]);

  const mergeSubtreeProductsIntoState = useCallback((rows) => {
    if (!rows?.length) return;
    setProductsByCategoryId((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        const kid = row.kategoriId != null ? String(row.kategoriId) : UNCategorized_KEY;
        const existing = next[kid] || [];
        const id = getRowId(row);
        if (!existing.some((r) => getRowId(r) === id)) {
          next[kid] = [...existing, row];
        }
      });
      return next;
    });
    rows.forEach((row) => {
      const kid = row.kategoriId != null ? String(row.kategoriId) : UNCategorized_KEY;
      contentsLoadedForCategoryRef.current.add(kid);
    });
  }, []);

  const loadAllProductsInSubtree = useCallback(
    async (categoryIdStr) => {
      const cached = subtreeProductIdsRef.current.get(categoryIdStr);
      if (cached) return cached;

      const useSubtree = categoryIdStr !== UNCategorized_KEY;
      const aggregated = await fetchAllBakimFiyatlariForCategory(categoryIdStr, {
        kategoriAgaciDahil: useSubtree,
      });
      const productIds = aggregated.map((row) => getRowId(row));
      subtreeProductIdsRef.current.set(categoryIdStr, productIds);
      mergeSubtreeProductsIntoState(aggregated);
      return productIds;
    },
    [mergeSubtreeProductsIntoState],
  );

  useEffect(() => {
    if (!isSearchActive) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    (async () => {
      try {
        const res = await fetchBakimFiyatlari({ arama: debouncedArama, limit: 500, sayfa: 1 });
        if (!cancelled) setSearchResults(res?.veri?.kayitlar || []);
      } catch (err) {
        if (!cancelled) {
          window.showNotification?.('error', getErrorMessage(err, 'Arama başarısız'));
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedArama, isSearchActive]);

  const categoryTree = useMemo(() => buildCategoryTree(categories), [categories]);

  const uncategorizedProducts = productsByCategoryId[UNCategorized_KEY] || [];

  const getProductIdsInSubtree = useCallback(
    (categoryIdStr) => {
      const cached = subtreeProductIdsRef.current.get(categoryIdStr);
      if (cached) return cached;

      const descendantIds = getDescendantCategoryIdsFromFlat(categoryIdStr, categories);
      const ids = [];
      descendantIds.forEach((cid) => {
        (productsByCategoryId[cid] || []).forEach((row) => ids.push(getRowId(row)));
      });
      return ids;
    },
    [categories, productsByCategoryId],
  );

  const getSubtreeCheckState = useCallback(
    (categoryIdStr) => {
      const ids = getProductIdsInSubtree(categoryIdStr);
      if (ids.length === 0) return { checked: false, indeterminate: false };
      const selectedCount = ids.filter((id) => selectedIds.has(id)).length;
      if (selectedCount === 0) return { checked: false, indeterminate: false };
      if (selectedCount === ids.length) return { checked: true, indeterminate: false };
      return { checked: false, indeterminate: true };
    },
    [getProductIdsInSubtree, selectedIds],
  );

  const toggleCategoryExpanded = useCallback(
    async (categoryId, isExpandedNow) => {
      const categoryIdStr = String(categoryId);
      setExpandedCategoryIds((prev) => {
        const next = new Set(prev);
        if (isExpandedNow) next.delete(categoryIdStr);
        else next.add(categoryIdStr);
        return next;
      });
      if (isExpandedNow) return;

      if (!fetchedCategoryChildrenRef.current.has(categoryIdStr)) {
        fetchedCategoryChildrenRef.current.add(categoryIdStr);
        try {
          const res = await fetchCategories({ kategoriTipi: 'urun', ustKategoriId: categoryIdStr });
          const raw = res?.veri?.kategoriler || res?.veri || [];
          const arr = Array.isArray(raw) ? raw : [];
          if (arr.length === 0) {
            setConfirmedLeafCategoryIds((prev) => new Set(prev).add(categoryIdStr));
            await loadProductsForCategory(categoryIdStr);
          } else {
            setCategories((prev) => mergeCategoriesDedup(prev, arr));
          }
        } catch (err) {
          fetchedCategoryChildrenRef.current.delete(categoryIdStr);
          window.showNotification?.('error', getErrorMessage(err, 'Alt kategoriler yüklenemedi'));
        }
      } else if (confirmedLeafCategoryIds.has(categoryIdStr)) {
        await loadProductsForCategory(categoryIdStr);
      }
    },
    [confirmedLeafCategoryIds, loadProductsForCategory],
  );

  const toggleUncategorizedExpanded = useCallback(async () => {
    setExpandedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(UNCategorized_KEY)) next.delete(UNCategorized_KEY);
      else next.add(UNCategorized_KEY);
      return next;
    });
    if (!contentsLoadedForCategoryRef.current.has(UNCategorized_KEY)) {
      await loadProductsForCategory(UNCategorized_KEY);
    }
  }, [loadProductsForCategory]);

  const toggleProductSelection = useCallback((idStr, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(idStr);
      else next.delete(idStr);
      return next;
    });
  }, []);

  const toggleCategorySelection = useCallback(
    async (categoryIdStr, checked) => {
      setCategorySelectLoadingIds((prev) => new Set(prev).add(categoryIdStr));
      try {
        const productIds = await loadAllProductsInSubtree(categoryIdStr);
        setSelectedIds((prev) => {
          const next = new Set(prev);
          productIds.forEach((id) => {
            if (checked) next.add(id);
            else next.delete(id);
          });
          return next;
        });
      } finally {
        setCategorySelectLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(categoryIdStr);
          return next;
        });
      }
    },
    [loadAllProductsInSubtree],
  );

  const toggleAllInCategory = useCallback((rows, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      (rows || []).forEach((row) => {
        const idStr = getRowId(row);
        if (checked) next.add(idStr);
        else next.delete(idStr);
      });
      return next;
    });
  }, []);

  const startEdit = (row) => {
    setEditId(getRowId(row));
    setEditForm({
      bakimFiyati: row.bakimFiyati != null ? String(row.bakimFiyati) : '',
      fabrikaPayi: row.fabrikaPayi != null ? String(row.fabrikaPayi) : '',
    });
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditForm({ bakimFiyati: '', fabrikaPayi: '' });
  };

  const patchLocalRows = useCallback((ids, payload) => {
    const idSet = new Set(ids.map(String));
    const apply = (row) => {
      if (!idSet.has(getRowId(row))) return row;
      return {
        ...row,
        ...(payload.bakimFiyati !== undefined ? { bakimFiyati: payload.bakimFiyati } : {}),
        ...(payload.fabrikaPayi !== undefined ? { fabrikaPayi: payload.fabrikaPayi } : {}),
      };
    };
    setProductsByCategoryId((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        next[key] = (next[key] || []).map(apply);
      });
      return next;
    });
    setSearchResults((prev) => prev.map(apply));
  }, []);

  const handleSave = async (row) => {
    const id = getRowId(row);
    setSaving(true);
    try {
      const payload = {};
      payload.bakimFiyati = editForm.bakimFiyati.trim() === '' ? null : Number(editForm.bakimFiyati);
      payload.fabrikaPayi = editForm.fabrikaPayi.trim() === '' ? null : Number(editForm.fabrikaPayi);
      if (
        (payload.bakimFiyati != null && (!Number.isFinite(payload.bakimFiyati) || payload.bakimFiyati < 0))
        || (payload.fabrikaPayi != null && (!Number.isFinite(payload.fabrikaPayi) || payload.fabrikaPayi < 0))
      ) {
        window.showNotification?.('error', 'Fiyatlar 0 veya daha büyük olmalıdır');
        return;
      }
      await updateBakimFiyati(id, payload);
      patchLocalRows([id], payload);
      window.showNotification?.('success', 'Bakım fiyatları kaydedildi');
      cancelEdit();
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Kaydedilemedi'));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkSubmit = async (payload) => {
    const ids = Array.from(selectedIds);
    setBulkSaving(true);
    try {
      await updateBakimFiyatiToplu({ icerikIds: ids, ...payload });
      patchLocalRows(ids, payload);
      window.showNotification?.('success', `${ids.length} ürünün fiyatları güncellendi`);
      setBulkModalOpen(false);
      setSelectedIds(new Set());
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Toplu güncelleme başarısız'));
    } finally {
      setBulkSaving(false);
    }
  };

  const renderProductTable = (rows, { categoryIdStr } = {}) => {
    const list = rows || [];
    const allSelected = list.length > 0 && list.every((row) => selectedIds.has(getRowId(row)));
    const someSelected = list.some((row) => selectedIds.has(getRowId(row)));

    return (
      <div className="table-container">
        <table className="admins-table admins-table--bulk-select bakim-products-table">
          <thead>
            <tr>
              <th className="ctp-bulk-check-col" scope="col">
                <input
                  type="checkbox"
                  className="ctp-bulk-check-input"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={(e) => toggleAllInCategory(list, e.target.checked)}
                  aria-label={categoryIdStr ? 'Bu kategorideki tüm ürünleri seç' : 'Tümünü seç'}
                />
              </th>
              <th>Ürün Kodu</th>
              <th>Ürün Adı</th>
              <th style={{ textAlign: 'right' }}>Bakım Fiyatı</th>
              <th style={{ textAlign: 'right' }}>Fabrika Payı</th>
              <th className="bakim-table-actions-col" aria-label="İşlemler" />
            </tr>
            {selectedIds.size > 0 && (
              <tr className="bakim-bulk-action-row">
                <th colSpan={6}>
                  <div className="bakim-bulk-action-row__inner">
                    <span>{selectedIds.size} ürün seçildi</span>
                    <button
                      type="button"
                      className="primary-btn bakim-bulk-assign-btn"
                      onClick={() => setBulkModalOpen(true)}
                    >
                      Seçilenlere fiyat ata
                    </button>
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={() => setSelectedIds(new Set())}
                    >
                      Seçimi temizle
                    </button>
                  </div>
                </th>
              </tr>
            )}
          </thead>
          <tbody>
            {list.map((row) => {
              const id = getRowId(row);
              const isEditing = editId === id;
              const isChecked = selectedIds.has(id);
              return (
                <tr key={id} className={isChecked ? 'ctp-bulk-row--selected' : isEditing ? 'bakim-edit-row' : undefined}>
                  <td className="ctp-bulk-check-col">
                    <input
                      type="checkbox"
                      className="ctp-bulk-check-input"
                      checked={isChecked}
                      onChange={(e) => toggleProductSelection(id, e.target.checked)}
                      aria-label={`${row.icerikAdi || row.urunAdi} seç`}
                    />
                  </td>
                  <td>{row.icerikKodu || row.urunKodu || '—'}</td>
                  <td>{row.icerikAdi || row.urunAdi || '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {isEditing ? (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="bakim-price-input"
                        value={editForm.bakimFiyati}
                        onChange={(e) => setEditForm((f) => ({ ...f, bakimFiyati: e.target.value }))}
                      />
                    ) : (
                      formatCurrency(row.bakimFiyati)
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {isEditing ? (
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="bakim-price-input"
                        value={editForm.fabrikaPayi}
                        onChange={(e) => setEditForm((f) => ({ ...f, fabrikaPayi: e.target.value }))}
                      />
                    ) : (
                      formatCurrency(row.fabrikaPayi)
                    )}
                  </td>
                  <td className="table-actions">
                    {isEditing ? (
                      <div className="bakim-row-actions">
                        <button
                          type="button"
                          className="category-action-btn edit-btn"
                          disabled={saving}
                          onClick={() => handleSave(row)}
                        >
                          Kaydet
                        </button>
                        <button type="button" className="category-action-btn" disabled={saving} onClick={cancelEdit}>
                          İptal
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="category-action-btn edit-btn"
                        onClick={() => startEdit(row)}
                        title="Düzenle"
                      >
                        <i className="fas fa-pen" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCategoryContentsBody = (leafRows, isLoading, categoryIdStr) => {
    if (isLoading) {
      return (
        <div className="content-accordion-leaf-loading" role="status" aria-live="polite">
          <i className="fas fa-spinner fa-spin" /> Yükleniyor…
        </div>
      );
    }
    if (leafRows.length === 0) {
      return <p className="content-accordion-hint">Bu kategoride ürün yok.</p>;
    }
    return renderProductTable(leafRows, { categoryIdStr });
  };

  const renderAccordionTree = (tree, level = 0) => {
    if (!tree || tree.length === 0) return null;
    const paddingLeftPx = level * 20 + 12;

    return tree.map((category) => {
      const categoryId = category.id || category._id;
      const categoryIdStr = String(categoryId);
      const hasChildrenInTree = category.children && category.children.length > 0;
      const isConfirmedLeaf = confirmedLeafCategoryIds.has(categoryIdStr);
      const treatAsBranch = hasChildrenInTree || !isConfirmedLeaf;
      const isExpanded = expandedCategoryIds.has(categoryIdStr);
      const leafRows = productsByCategoryId[categoryIdStr] || [];
      const isLeafLoading = leafContentsLoadingIds.has(categoryIdStr);
      const catCheckState = getSubtreeCheckState(categoryIdStr);
      const catSelectLoading = categorySelectLoadingIds.has(categoryIdStr);

      if (!treatAsBranch) {
        return (
          <div key={categoryId} className="content-accordion-item">
            <div className="content-accordion-leaf">
              <div
                className="content-accordion-row"
                style={{ paddingLeft: `${paddingLeftPx}px` }}
              >
                <CategoryCheckbox
                  checked={catCheckState.checked}
                  indeterminate={catCheckState.indeterminate}
                  disabled={catSelectLoading}
                  ariaLabel={`${category.kategoriAdi} altındaki tüm ürünleri seç`}
                  onChange={(e) => toggleCategorySelection(categoryIdStr, e.target.checked)}
                />
                <button
                  type="button"
                  className="category-toggle-btn"
                  onClick={() => toggleCategoryExpanded(categoryId, isExpanded)}
                  aria-expanded={isExpanded}
                >
                  <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
                </button>
                <span
                  className="content-accordion-title bakim-accordion-title-btn"
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleCategoryExpanded(categoryId, isExpanded)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleCategoryExpanded(categoryId, isExpanded);
                    }
                  }}
                >
                  {category.kategoriAdi}
                </span>
              </div>
              {isExpanded && renderCategoryContentsBody(leafRows, isLeafLoading, categoryIdStr)}
            </div>
          </div>
        );
      }

      return (
        <div key={categoryId} className="content-accordion-item">
          <div
            className="content-accordion-row"
            style={{ paddingLeft: `${paddingLeftPx}px` }}
          >
            <CategoryCheckbox
              checked={catCheckState.checked}
              indeterminate={catCheckState.indeterminate}
              disabled={catSelectLoading}
              ariaLabel={`${category.kategoriAdi} grubundaki tüm ürünleri seç`}
              onChange={(e) => toggleCategorySelection(categoryIdStr, e.target.checked)}
            />
            <button
              type="button"
              className="category-toggle-btn"
              onClick={() => toggleCategoryExpanded(categoryId, isExpanded)}
              aria-expanded={isExpanded}
            >
              <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`} />
            </button>
            <span
              className="content-accordion-title bakim-accordion-title-btn"
              role="button"
              tabIndex={0}
              onClick={() => toggleCategoryExpanded(categoryId, isExpanded)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleCategoryExpanded(categoryId, isExpanded);
                }
              }}
            >
              {category.kategoriAdi}
            </span>
            {catSelectLoading ? (
              <i className="fas fa-spinner fa-spin bakim-cat-loading" aria-hidden />
            ) : null}
          </div>
          {isExpanded && (
            <div className="content-accordion-children">
              {renderAccordionTree(category.children, level + 1)}
              {isConfirmedLeaf && (
                <div className="content-accordion-direct-contents">
                  {renderCategoryContentsBody(leafRows, isLeafLoading, categoryIdStr)}
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  const uncategorizedExpanded = expandedCategoryIds.has(UNCategorized_KEY);
  const uncategorizedCheckState = getSubtreeCheckState(UNCategorized_KEY);

  return (
    <div className="bakim-yonetimi-page">
      <div className="page-header">
        <h1>Bakım Yönetimi</h1>
        <p className="page-subtitle">
          Ürünleri kategori bazında yönetin; tekil veya toplu bakım fiyatı atayın.
        </p>
      </div>

      <div className="bakim-info-banner">
        <strong>Bakım fiyatı</strong> teknisyen randevusunda müşteriye gösterilir; OTP ile tamamlanınca ödeme alınmış sayılır.
        {' '}
        <strong>Fabrika payı</strong> hakediş değildir — OTP sonrası teknik servisin cari hesabına otomatik borç olarak yazılır.
      </div>

      <div className="ctp-toolbar bakim-toolbar">
        <input
          type="search"
          className="bakim-search-input"
          placeholder="Ürün adı veya kodu ara..."
          value={arama}
          onChange={(e) => setArama(e.target.value)}
        />
        {selectedIds.size > 0 && !isSearchActive && (
          <button type="button" className="primary-btn" onClick={() => setBulkModalOpen(true)}>
            Seçilenlere fiyat ata ({selectedIds.size})
          </button>
        )}
      </div>

      <div className="card">
        {isSearchActive ? (
          searchLoading ? (
            <div className="bakim-center-msg">Yükleniyor…</div>
          ) : searchResults.length === 0 ? (
            <div className="bakim-center-msg">Arama sonucu bulunamadı</div>
          ) : (
            renderProductTable(searchResults)
          )
        ) : categoriesLoading ? (
          <div className="bakim-center-msg">Yükleniyor…</div>
        ) : categoryTree.length === 0 && uncategorizedProducts.length === 0 ? (
          <div className="bakim-center-msg">Henüz ürün kategorisi bulunamadı</div>
        ) : (
          <div className="content-accordion bakim-accordion">
            {renderAccordionTree(categoryTree)}
            <div className="content-accordion-item content-accordion-item--uncategorized">
              <div className="content-accordion-leaf">
                <div
                  className="content-accordion-row"
                  style={{ paddingLeft: '12px' }}
                >
                  <CategoryCheckbox
                    checked={uncategorizedCheckState.checked}
                    indeterminate={uncategorizedCheckState.indeterminate}
                    disabled={categorySelectLoadingIds.has(UNCategorized_KEY)}
                    ariaLabel="Kategorisiz tüm ürünleri seç"
                    onChange={(e) => toggleCategorySelection(UNCategorized_KEY, e.target.checked)}
                  />
                  <button
                    type="button"
                    className="category-toggle-btn"
                    onClick={toggleUncategorizedExpanded}
                    aria-expanded={uncategorizedExpanded}
                  >
                    <i className={`fas fa-chevron-${uncategorizedExpanded ? 'down' : 'right'}`} />
                  </button>
                  <span
                    className="content-accordion-title bakim-accordion-title-btn"
                    role="button"
                    tabIndex={0}
                    onClick={toggleUncategorizedExpanded}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleUncategorizedExpanded();
                      }
                    }}
                  >
                    Kategorisiz
                  </span>
                </div>
                {uncategorizedExpanded
                  && renderCategoryContentsBody(
                    uncategorizedProducts,
                    leafContentsLoadingIds.has(UNCategorized_KEY),
                    UNCategorized_KEY,
                  )}
              </div>
            </div>
          </div>
        )}
      </div>

      <BakimFiyatTopluAtaModal
        open={bulkModalOpen}
        selectedCount={selectedIds.size}
        saving={bulkSaving}
        onClose={() => !bulkSaving && setBulkModalOpen(false)}
        onSubmit={handleBulkSubmit}
      />
    </div>
  );
};

export default BakimYonetimiPage;
